// DashboardClient.jsx
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Eye, Settings, ToggleLeft, ToggleRight, X, Zap, Search, MapPin, Star, Phone, FileText, Plus, Send, CheckCircle, Clock } from 'lucide-react';
import { bookingService, prestataireService } from '../services/api';
import { useAuthStore } from '../store/authStore';
import MessagingPanel from '../components/MessagingPanel';
import toast from 'react-hot-toast';
import api from '../services/api';

const STATUS_LABELS = {
  PENDING:     { label: 'En attente',  color: '#F59E0B', icon: '⏳' },
  CONFIRMED:   { label: 'Confirmée',   color: '#3B82F6', icon: '✅' },
  IN_PROGRESS: { label: 'En cours',    color: '#8B5CF6', icon: '🔧' },
  COMPLETED:   { label: 'Terminée',    color: '#10B981', icon: '🎉' },
  CANCELLED:   { label: 'Annulée',     color: '#EF4444', icon: '❌' },
};

const SERVICES = [
  { key: 'MECANIQUE_MOTO',     label: '🏍️ Mécanique Moto' },
  { key: 'MECANIQUE_VOITURE',  label: '🚗 Mécanique Voiture' },
  { key: 'VULCANISATEUR',      label: '🔩 Vulcanisateur' },
  { key: 'ELECTRICITE_AUTO',   label: '⚡ Électricité Auto' },
  { key: 'CARROSSERIE',        label: '🎨 Carrosserie' },
  { key: 'CLIMATISATION',      label: '❄️ Climatisation' },
  { key: 'VIDANGE_ENTRETIEN',  label: '🔧 Vidange & Entretien' },
  { key: 'PIECES_DETACHEES',   label: '⚙️ Pièces détachées' },
];

// ── MODAL LANCER PRESTATION ──────────────────────────────
function BookingModal({ onClose, onSuccess }) {
  const [step, setStep] = useState(1); // 1=service, 2=prestataire, 3=details
  const [selectedService, setSelectedService] = useState('');
  const [prestataires, setPrestataires] = useState([]);
  const [loadingP, setLoadingP] = useState(false);
  const [selectedP, setSelectedP] = useState(null);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');

  const fetchPrestataires = async (service) => {
    setLoadingP(true);
    try {
      const { data } = await api.get(`/prestataires?service=${service}&limit=20`);
      setPrestataires(data.data || []);
    } catch { toast.error('Erreur chargement prestataires'); }
    finally { setLoadingP(false); }
  };

  const handleSelectService = (key) => {
    setSelectedService(key);
    fetchPrestataires(key);
    setStep(2);
  };

  const handleSelectP = (p) => {
    setSelectedP(p);
    setStep(3);
  };

  const handleSubmit = async () => {
    if (!description.trim()) { toast.error('Décrivez votre problème.'); return; }
    setSubmitting(true);
    try {
      await bookingService.create({
        prestataireId: selectedP.id,
        service: selectedService,
        description,
      });
      toast.success('🎉 Demande envoyée ! Le prestataire va vous contacter.');
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de la demande.');
    } finally { setSubmitting(false); }
  };

  const filtered = prestataires.filter(p =>
    !search || `${p.firstName} ${p.lastName} ${p.shopName} ${p.city}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(10,20,35,0.85)',
        zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem', backdropFilter: 'blur(8px)',
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.92, y: 30 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.92, y: 30 }}
        style={{
          background: 'white', borderRadius: '20px', width: '100%', maxWidth: '640px',
          maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
          boxShadow: '0 40px 100px rgba(0,0,0,0.4)',
        }}
      >
        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg, var(--blue-dark), #0D1B2A)', padding: '1.5rem 2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div>
            <div style={{ color: 'var(--orange)', fontFamily: 'var(--font-cond)', fontSize: '0.75rem', letterSpacing: '2px', marginBottom: '0.3rem' }}>
              ÉTAPE {step}/3
            </div>
            <h2 style={{ color: 'white', fontFamily: 'var(--font-display)', fontSize: '1.4rem', letterSpacing: '1px', margin: 0 }}>
              {step === 1 && '⚡ CHOISIR UN SERVICE'}
              {step === 2 && '👨‍🔧 CHOISIR UN PRESTATAIRE'}
              {step === 3 && '📝 DÉCRIRE VOTRE PROBLÈME'}
            </h2>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', width: 36, height: 36, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={18} />
          </button>
        </div>

        {/* Progress bar */}
        <div style={{ height: 3, background: 'var(--gray-mid)' }}>
          <motion.div animate={{ width: `${(step / 3) * 100}%` }} style={{ height: '100%', background: 'var(--orange)' }} transition={{ duration: 0.3 }} />
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem 2rem' }}>

          {/* STEP 1 - Service */}
          {step === 1 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '0.8rem' }}>
              {SERVICES.map(s => (
                <button key={s.key} onClick={() => handleSelectService(s.key)}
                  style={{
                    padding: '1.2rem 0.8rem', border: '2px solid var(--gray-mid)', borderRadius: '12px',
                    background: 'white', cursor: 'pointer', textAlign: 'center',
                    fontFamily: 'var(--font-cond)', fontWeight: 700, fontSize: '0.85rem', color: 'var(--blue-dark)',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--orange)'; e.currentTarget.style.background = 'rgba(255,107,0,0.05)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--gray-mid)'; e.currentTarget.style.background = 'white'; }}
                >
                  <div style={{ fontSize: '1.8rem', marginBottom: '0.5rem' }}>{s.label.split(' ')[0]}</div>
                  {s.label.split(' ').slice(1).join(' ')}
                </button>
              ))}
            </div>
          )}

          {/* STEP 2 - Prestataire */}
          {step === 2 && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', background: 'var(--gray-light)', borderRadius: '10px', padding: '0.7rem 1rem', marginBottom: '1rem' }}>
                <Search size={16} color="var(--gray-dark)" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un prestataire..."
                  style={{ border: 'none', background: 'none', outline: 'none', flex: 1, fontFamily: 'var(--font-body)', fontSize: '0.9rem' }} />
              </div>
              {loadingP ? (
                <div style={{ textAlign: 'center', padding: '3rem' }}>
                  <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} style={{ fontSize: '2rem', display: 'inline-block' }}>⚙️</motion.div>
                </div>
              ) : filtered.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--gray-text)' }}>
                  Aucun prestataire disponible pour ce service.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                  {filtered.map(p => (
                    <button key={p.id} onClick={() => handleSelectP(p)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem',
                        border: '2px solid var(--gray-mid)', borderRadius: '12px',
                        background: 'white', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--orange)'; e.currentTarget.style.background = 'rgba(255,107,0,0.03)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--gray-mid)'; e.currentTarget.style.background = 'white'; }}
                    >
                      <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'linear-gradient(135deg, var(--blue-dark), #0D5B8E)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontFamily: 'var(--font-display)', fontSize: '1.2rem', flexShrink: 0, overflow: 'hidden' }}>
                        {p.profilePhotoUrl ? <img src={p.profilePhotoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : p.firstName?.[0]}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontFamily: 'var(--font-cond)', fontWeight: 700, color: 'var(--blue-dark)', fontSize: '0.95rem' }}>
                          {p.firstName} {p.lastName} {p.shopName && `— ${p.shopName}`}
                        </div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--gray-text)', display: 'flex', gap: '0.8rem', marginTop: '0.2rem' }}>
                          <span><MapPin size={11} style={{ display: 'inline' }} /> {p.city}, {p.neighborhood}</span>
                          {p.ratingAvg > 0 && <span><Star size={11} style={{ display: 'inline' }} /> {p.ratingAvg.toFixed(1)}</span>}
                          {p.isAvailable ? <span style={{ color: '#10B981' }}>🟢 Disponible</span> : <span style={{ color: '#EF4444' }}>🔴 Indisponible</span>}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              <button onClick={() => setStep(1)} style={{ background: 'none', border: 'none', color: 'var(--orange)', cursor: 'pointer', marginTop: '1rem', fontSize: '0.85rem', fontWeight: 600 }}>
                ← Changer de service
              </button>
            </>
          )}

          {/* STEP 3 - Description */}
          {step === 3 && selectedP && (
            <>
              {/* Recap prestataire */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', background: 'rgba(26,59,93,0.05)', borderRadius: '12px', marginBottom: '1.5rem', border: '1px solid rgba(26,59,93,0.1)' }}>
                <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--blue-dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontFamily: 'var(--font-display)', fontSize: '1.1rem', flexShrink: 0, overflow: 'hidden' }}>
                  {selectedP.profilePhotoUrl ? <img src={selectedP.profilePhotoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : selectedP.firstName?.[0]}
                </div>
                <div>
                  <div style={{ fontFamily: 'var(--font-cond)', fontWeight: 700, color: 'var(--blue-dark)' }}>{selectedP.firstName} {selectedP.lastName}</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--gray-text)' }}>{SERVICES.find(s => s.key === selectedService)?.label} • {selectedP.city}</div>
                </div>
                {selectedP.user?.phone && (
                  <a href={`tel:${selectedP.user.phone}`} style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#10B981', fontWeight: 600, fontSize: '0.82rem', textDecoration: 'none' }}>
                    <Phone size={14} /> {selectedP.user.phone}
                  </a>
                )}
              </div>

              <label style={{ fontFamily: 'var(--font-cond)', fontWeight: 700, color: 'var(--blue-dark)', fontSize: '0.8rem', letterSpacing: '1px', textTransform: 'uppercase', display: 'block', marginBottom: '0.5rem' }}>
                Décrivez votre problème *
              </label>
              <textarea
                value={description} onChange={e => setDescription(e.target.value)}
                placeholder="Ex: Ma moto ne démarre plus, j'ai une fuite d'huile, le moteur fait un bruit bizarre..."
                rows={5}
                style={{ width: '100%', padding: '1rem', border: '2px solid var(--gray-mid)', borderRadius: '10px', fontFamily: 'var(--font-body)', fontSize: '0.9rem', outline: 'none', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.6 }}
                onFocus={e => e.target.style.borderColor = 'var(--orange)'}
                onBlur={e => e.target.style.borderColor = 'var(--gray-mid)'}
              />

              <button onClick={() => setStep(2)} style={{ background: 'none', border: 'none', color: 'var(--orange)', cursor: 'pointer', marginTop: '1rem', fontSize: '0.85rem', fontWeight: 600 }}>
                ← Changer de prestataire
              </button>
            </>
          )}
        </div>

        {/* Footer */}
        {step === 3 && (
          <div style={{ padding: '1.2rem 2rem', borderTop: '1px solid var(--gray-mid)', flexShrink: 0 }}>
            <button onClick={handleSubmit} disabled={submitting}
              style={{
                width: '100%', padding: '1rem', background: 'linear-gradient(135deg, var(--orange), #CC4400)',
                border: 'none', borderRadius: '12px', color: 'white',
                fontFamily: 'var(--font-cond)', fontWeight: 700, fontSize: '1.05rem', letterSpacing: '1px',
                cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem',
                boxShadow: '0 4px 20px rgba(255,107,0,0.35)',
              }}
            >
              <Zap size={18} />
              {submitting ? 'ENVOI EN COURS...' : '🚀 LANCER LA PRESTATION DE SERVICE'}
            </button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

// ── DASHBOARD CLIENT ──────────────────────────────────────
export function DashboardClient() {
  const { user } = useAuthStore();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('bookings');
  const [messageContact, setMessageContact] = useState(null);
  const [unread, setUnread] = useState(0);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [clientInvoices, setClientInvoices] = useState([]);

  const loadBookings = () => {
    bookingService.getMy().then(r => setBookings(r.data)).catch(console.error).finally(() => setLoading(false));
  };

  const loadClientInvoices = async () => {
    try { const { data } = await api.get('/invoices/client'); setClientInvoices(data); } catch {}
  };

  useEffect(() => {
    loadBookings();
    loadClientInvoices();
    api.get('/messages/conversations').then(({ data }) => {
      setUnread(data.filter(c => !c.isRead && c.receiverId === user?.id).length);
    }).catch(() => {});
  }, [user?.id]);

  const openMessages = (contact) => { setMessageContact(contact); setActiveTab('messages'); };


  const TABS = [
    { key: 'bookings', label: 'MES DEMANDES', icon: '📋' },
    { key: 'factures', label: 'MES FACTURES', icon: '🧾' },
    { key: 'messages', label: 'MESSAGES',     icon: '💬' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--gray-light)', paddingTop: '80px' }}>

      {/* MODAL */}
      <AnimatePresence>
        {showBookingModal && (
          <BookingModal onClose={() => setShowBookingModal(false)} onSuccess={loadBookings} />
        )}
      </AnimatePresence>

      <div style={{ background: 'linear-gradient(135deg, var(--blue-dark), #0D1B2A)', padding: '3rem 0 0' }}>
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.2rem' }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'linear-gradient(135deg,#1A3B5D,#0D5B8E)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontFamily: 'var(--font-display)', fontSize: '1.4rem', border: '3px solid rgba(255,255,255,0.2)', flexShrink: 0 }}>
                {user?.firstName?.charAt(0)?.toUpperCase()}
              </div>
              <div>
                <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.8rem,5vw,3.5rem)', color: 'white', letterSpacing: '2px', lineHeight: 1 }}>
                  BONJOUR, <span style={{ color: 'var(--orange)' }}>{user?.firstName?.toUpperCase()} 👋</span>
                </h1>
                <p style={{ color: 'rgba(255,255,255,0.5)', marginTop: '0.4rem', fontSize: '0.85rem' }}>Espace client — gérez vos demandes</p>
              </div>
            </div>

            {/* BOUTON LANCER PRESTATION */}
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setShowBookingModal(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.7rem',
                background: 'linear-gradient(135deg, var(--orange), #CC4400)',
                border: 'none', borderRadius: '14px', color: 'white',
                padding: '0.9rem 1.8rem', cursor: 'pointer',
                fontFamily: 'var(--font-cond)', fontWeight: 700, fontSize: '1rem', letterSpacing: '1px',
                boxShadow: '0 6px 24px rgba(255,107,0,0.45)',
              }}
            >
              <Zap size={20} />
              🔧 LANCER UNE PRESTATION
            </motion.button>
          </motion.div>

          <div style={{ display: 'flex', gap: 0 }}>
            {TABS.map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                style={{ background: activeTab === tab.key ? 'rgba(255,107,0,0.15)' : 'transparent', border: 'none', borderBottom: activeTab === tab.key ? '3px solid #FF6B00' : '3px solid transparent', color: activeTab === tab.key ? 'white' : 'rgba(255,255,255,0.5)', padding: '0.9rem 1.5rem', cursor: 'pointer', fontFamily: 'var(--font-cond)', fontWeight: 700, fontSize: '0.88rem', letterSpacing: '1.5px', display: 'flex', alignItems: 'center', gap: '0.5rem', transition: 'all 0.2s' }}>
                {tab.icon} {tab.label}
                {tab.key === 'messages' && unread > 0 && <span style={{ background: '#FF6B00', color: 'white', borderRadius: '100px', padding: '1px 7px', fontSize: '0.68rem', fontWeight: 700 }}>{unread}</span>}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="container" style={{ padding: '2.5rem 1.5rem 5rem' }}>
        {activeTab === 'bookings' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', color: 'var(--blue-dark)', letterSpacing: '1px' }}>MES DEMANDES ({bookings.length})</h2>
              <button onClick={() => setShowBookingModal(true)} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Zap size={16} /> Nouvelle demande
              </button>
            </div>
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} style={{ fontSize: '3rem' }}>⚙️</motion.div>
              </div>
            ) : bookings.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '5rem 2rem', background: 'white', borderRadius: 16 }}>
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🔧</div>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', color: 'var(--blue-dark)', letterSpacing: '1px' }}>AUCUNE DEMANDE</h3>
                <p style={{ color: 'var(--gray-text)', marginBottom: '1.5rem' }}>Lancez votre première prestation de service !</p>
                <button onClick={() => setShowBookingModal(true)} className="btn btn-primary btn-lg" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.6rem' }}>
                  <Zap size={18} /> 🔧 Lancer une prestation
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {bookings.map((b, i) => (
                  <motion.div key={b.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}
                    style={{ background: 'white', borderRadius: 14, padding: '1.5rem', boxShadow: 'var(--shadow-sm)', border: '1px solid rgba(26,59,93,0.06)', display: 'flex', gap: '1.2rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div style={{ fontFamily: 'var(--font-cond)', fontWeight: 700, fontSize: '1.05rem', color: 'var(--blue-dark)', marginBottom: '0.3rem' }}>
                        {b.prestataire?.firstName} {b.prestataire?.lastName}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--gray-text)' }}>{b.service?.replace(/_/g, ' ')} • {b.prestataire?.city}</div>
                      {b.description && <div style={{ fontSize: '0.82rem', color: 'var(--gray-text)', marginTop: '0.3rem', fontStyle: 'italic' }}>"{b.description}"</div>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.75rem', color: 'var(--gray-text)' }}>{new Date(b.createdAt).toLocaleDateString('fr-FR')}</span>
                      <span style={{ padding: '0.35rem 1rem', borderRadius: 20, fontSize: '0.75rem', fontFamily: 'var(--font-cond)', fontWeight: 700, background: `${STATUS_LABELS[b.status]?.color}18`, color: STATUS_LABELS[b.status]?.color, border: `1px solid ${STATUS_LABELS[b.status]?.color}40` }}>
                        {STATUS_LABELS[b.status]?.icon} {STATUS_LABELS[b.status]?.label || b.status}
                      </span>
                      {(b.status === 'PENDING' || b.status === 'CONFIRMED') && (
                        <button
                          onClick={async () => {
                            try {
                              await bookingService.updateStatus(b.id, 'CANCELLED');
                              setBookings(prev => prev.map(x => x.id === b.id ? { ...x, status: 'CANCELLED' } : x));
                              toast.success('Demande annulée.');
                            } catch { toast.error("Erreur lors de l'annulation."); }
                          }}
                          style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#EF4444', padding: '0.4rem 0.9rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, fontFamily: 'var(--font-cond)' }}>
                          ❌ Annuler
                        </button>
                      )}
                      {b.prestataire?.user && (
                        <button onClick={() => openMessages(b.prestataire.user)}
                          style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: 'rgba(255,107,0,0.1)', border: '1px solid rgba(255,107,0,0.3)', color: 'var(--orange)', padding: '0.4rem 0.9rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>
                          <MessageCircle size={14} /> Message
                        </button>
                      )}
                      <Link to={`/prestataires/${b.prestataireId}`} className="btn btn-ghost btn-sm" style={{ border: '1px solid var(--gray-mid)' }}>Voir profil</Link>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}
        {activeTab === 'factures' && (
          <div>
            <h2 style={{ fontFamily:'var(--font-display)', fontSize:'1.8rem', color:'var(--blue-dark)', letterSpacing:'1px', marginBottom:'1.5rem' }}>
              MES FACTURES ({clientInvoices.length})
            </h2>
            {clientInvoices.length === 0 ? (
              <div style={{ textAlign:'center', padding:'4rem 2rem', background:'white', borderRadius:16 }}>
                <div style={{ fontSize:'4rem', marginBottom:'1rem' }}>🧾</div>
                <p style={{ color:'var(--gray-text)' }}>Aucune facture reçue pour l'instant.</p>
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
                {clientInvoices.map((inv, i) => (
                  <motion.div key={inv.id} initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }} transition={{ delay:i*0.06 }}
                    style={{ background:'white', borderRadius:12, padding:'1.5rem', boxShadow:'var(--shadow-sm)', border:`2px solid ${inv.status==='PAID'?'rgba(16,185,129,0.2)':'rgba(255,107,0,0.2)'}`, display:'flex', gap:'1.2rem', alignItems:'center', flexWrap:'wrap', cursor:'pointer', transition:'transform 0.15s' }}
                    onClick={() => window.location.href=`/facture/${inv.id}`}>
                    <div style={{ flex:1, minWidth:180 }}>
                      <div style={{ fontFamily:'var(--font-cond)', fontWeight:700, fontSize:'1rem', color:'var(--blue-dark)', marginBottom:'0.2rem' }}>
                        {inv.service?.replace(/_/g,' ')}
                      </div>
                      {inv.description && <div style={{ fontSize:'0.83rem', color:'var(--gray-text)', fontStyle:'italic' }}>{inv.description}</div>}
                      <div style={{ fontSize:'0.72rem', color:'rgba(0,0,0,0.3)', marginTop:'0.3rem' }}>{new Date(inv.createdAt).toLocaleDateString('fr-FR')}</div>
                    </div>
                    <div style={{ textAlign:'right' }}>
                      <div style={{ fontFamily:'var(--font-display)', fontSize:'1.6rem', color:'var(--blue-dark)', letterSpacing:'1px', lineHeight:1 }}>
                        {inv.amount?.toLocaleString('fr-FR')} FCFA
                      </div>
                      <span style={{ display:'inline-flex', alignItems:'center', gap:'0.3rem', padding:'0.25rem 0.7rem', borderRadius:20, fontSize:'0.7rem', fontFamily:'var(--font-cond)', fontWeight:700, background:inv.status==='PAID'?'rgba(16,185,129,0.1)':'rgba(255,107,0,0.1)', color:inv.status==='PAID'?'#10B981':'#FF6B00' }}>
                        {inv.status==='PAID'?'✅ PAYÉE':'⏳ EN ATTENTE'}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'messages' && <MessagingPanel initialContact={messageContact} />}
      </div>
    </div>
  );
}

// ── DASHBOARD PRESTATAIRE ─────────────────────────────────
export function DashboardPrestataire() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [activeTab, setActiveTab] = useState('bookings');
  const [messageContact, setMessageContact] = useState(null);
  const [unread, setUnread] = useState(0);
  const [prestataireId, setPrestataireId] = useState(null);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [isAvailable, setIsAvailable] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({ clientName: '', clientPhone: '', service: '', description: '', amount: '', bookingId: '', clientUserId: '' });
  const [savingInvoice, setSavingInvoice] = useState(false);

  const loadData = async () => {
    try {
      const me = await api.get('/auth/me');
      const p = me.data.prestataire;
      setPrestataireId(p?.id);
      setProfilePhoto(p?.profilePhotoUrl);
      setIsAvailable(p?.isAvailable ?? true);

      prestataireService.getStats().then(r => setStats(r.data)).catch(console.error);
      bookingService.getMy().then(r => setBookings(r.data)).catch(console.error);
      api.get('/messages/conversations').then(({ data }) => {
        setUnread(data.filter(c => !c.isRead && c.receiverId === user?.id).length);
      }).catch(() => {});
    } catch (e) { console.error(e); }
  };

  useEffect(() => { loadData(); loadInvoices(); }, [user?.id]);

  const handleToggleAvailability = async () => {
    try {
      await prestataireService.toggleAvailability();
      setIsAvailable(prev => !prev);
      toast.success(isAvailable ? 'Vous êtes maintenant indisponible.' : 'Vous êtes maintenant disponible.');
    } catch { toast.error('Erreur toggle disponibilité'); }
  };

  const handleUpdateStatus = async (bookingId, status) => {
    setUpdatingStatus(bookingId);
    try {
      await bookingService.updateStatus(bookingId, status);
      setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, status } : b));
      toast.success(`Statut mis à jour : ${STATUS_LABELS[status]?.label}`);
    } catch {
      toast.error('Erreur mise à jour du statut.');
    } finally { setUpdatingStatus(null); }
  };

  const loadInvoices = async () => {
    try {
      const { data } = await api.get('/invoices');
      setInvoices(data);
    } catch {}
  };

  const handleCreateInvoice = async () => {
    if (!invoiceForm.clientName || !invoiceForm.amount || !invoiceForm.service) {
      toast.error('Remplissez les champs obligatoires.');
      return;
    }
    setSavingInvoice(true);
    try {
      await api.post('/invoices', {
        ...invoiceForm,
        amount: parseFloat(invoiceForm.amount),
      });
      toast.success('Facture créée avec succès !');
      setShowInvoiceForm(false);
      setInvoiceForm({ clientName: '', clientPhone: '', service: '', description: '', amount: '', bookingId: '' });
      loadInvoices();
    } catch {
      toast.error('Erreur lors de la création de la facture.');
    } finally { setSavingInvoice(false); }
  };

  const handleMarkPaid = async (invoiceId) => {
    try {
      await api.patch(`/invoices/${invoiceId}/paid`);
      setInvoices(prev => prev.map(inv => inv.id === invoiceId ? { ...inv, status: 'PAID' } : inv));
      toast.success('Facture marquée comme payée !');
    } catch { toast.error('Erreur.'); }
  };

  const TABS = [
    { key: 'bookings',    label: 'DEMANDES REÇUES', icon: '📋' },
    { key: 'facturation', label: 'FACTURATION',      icon: '🧾' },
    { key: 'messages',    label: 'MESSAGES',         icon: '💬' },
  ];

  const statCards = stats ? [
    { icon: '📋', label: 'Demandes totales', val: stats.totalBookings,    color: 'var(--blue-dark)' },
    { icon: '✅', label: 'Terminées',        val: stats.completedBookings, color: '#10B981' },
    { icon: '⭐', label: 'Note moyenne',     val: stats.ratingAvg?.toFixed(1) || 'N/A', color: '#FBBF24' },
    { icon: '💬', label: 'Messages non lus', val: unread, color: 'var(--orange)', clickable: true },
  ] : [];

  const openMessages = (contact) => { setMessageContact(contact); setActiveTab('messages'); };
  const initials = user?.firstName?.charAt(0)?.toUpperCase() || '?';

  return (
    <div style={{ minHeight: '100vh', background: 'var(--gray-light)', paddingTop: '80px' }}>

      <div style={{ background: 'linear-gradient(135deg, var(--blue-dark), #0D1B2A)', padding: '2.5rem 0 0' }}>
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.2rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
              <div style={{ position: 'relative', flexShrink: 0 }}>
                {profilePhoto ? (
                  <img src={profilePhoto} alt="Profil" style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', border: '3px solid #FF6B00', boxShadow: '0 6px 20px rgba(255,107,0,0.4)', display: 'block' }} />
                ) : (
                  <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'linear-gradient(135deg,#FF6B00,#CC4400)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontFamily: 'var(--font-display)', fontSize: '1.8rem', border: '3px solid rgba(255,255,255,0.2)', boxShadow: '0 6px 20px rgba(255,107,0,0.4)' }}>
                    {initials}
                  </div>
                )}
                <div style={{ position: 'absolute', bottom: 2, right: 2, width: '16px', height: '16px', borderRadius: '50%', background: isAvailable ? '#10B981' : '#EF4444', border: '2px solid #0D1B2A' }} />
              </div>

              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.3rem', flexWrap: 'wrap' }}>
                  <span style={{ background: 'rgba(255,107,0,0.15)', border: '1px solid rgba(255,107,0,0.4)', color: 'var(--orange)', padding: '0.2rem 0.7rem', borderRadius: '20px', fontSize: '0.68rem', fontFamily: 'var(--font-cond)', fontWeight: 700, letterSpacing: '1.5px' }}>ESPACE PRESTATAIRE</span>
                  <span style={{ background: isAvailable ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)', border: `1px solid ${isAvailable ? 'rgba(16,185,129,0.4)' : 'rgba(239,68,68,0.4)'}`, color: isAvailable ? '#10B981' : '#EF4444', padding: '0.2rem 0.7rem', borderRadius: '20px', fontSize: '0.68rem', fontFamily: 'var(--font-cond)', fontWeight: 700 }}>
                    {isAvailable ? '🟢 Disponible' : '🔴 Indisponible'}
                  </span>
                </div>
                <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.5rem,4vw,3rem)', color: 'white', letterSpacing: '2px', lineHeight: 1 }}>
                  MON TABLEAU <span style={{ color: 'var(--orange)' }}>DE BORD 🔧</span>
                </h1>
                <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.82rem', marginTop: '0.3rem' }}>Bienvenue, {user?.firstName} {user?.lastName}</p>
              </div>

              <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                <button onClick={() => navigate(`/prestataires/${prestataireId}`)}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.8)', padding: '0.6rem 1.1rem', borderRadius: '10px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, transition: 'all 0.2s' }}>
                  <Eye size={15} /> Voir mon profil
                </button>
                <button onClick={handleToggleAvailability}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: isAvailable ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)', border: `1px solid ${isAvailable ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`, color: isAvailable ? '#10B981' : '#EF4444', padding: '0.6rem 1.1rem', borderRadius: '10px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 700 }}>
                  {isAvailable ? <ToggleRight size={15} /> : <ToggleLeft size={15} />}
                  {isAvailable ? 'Passer indisponible' : 'Passer disponible'}
                </button>
              </div>
            </div>
          </motion.div>

          <div style={{ display: 'flex', gap: 0 }}>
            {TABS.map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                style={{ background: activeTab === tab.key ? 'rgba(255,107,0,0.15)' : 'transparent', border: 'none', borderBottom: activeTab === tab.key ? '3px solid #FF6B00' : '3px solid transparent', color: activeTab === tab.key ? 'white' : 'rgba(255,255,255,0.5)', padding: '0.9rem 1.5rem', cursor: 'pointer', fontFamily: 'var(--font-cond)', fontWeight: 700, fontSize: '0.88rem', letterSpacing: '1.5px', display: 'flex', alignItems: 'center', gap: '0.5rem', transition: 'all 0.2s' }}>
                {tab.icon} {tab.label}
                {tab.key === 'messages' && unread > 0 && <span style={{ background: '#FF6B00', color: 'white', borderRadius: '100px', padding: '1px 7px', fontSize: '0.68rem', fontWeight: 700 }}>{unread}</span>}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="container" style={{ padding: '2.5rem 1.5rem 5rem' }}>

        {activeTab === 'bookings' && (
          <>
            {stats && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.2rem', marginBottom: '2.5rem' }}>
                {statCards.map((s, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
                    onClick={() => s.clickable && setActiveTab('messages')}
                    style={{ background: 'white', borderRadius: 14, padding: '1.5rem', boxShadow: 'var(--shadow-sm)', textAlign: 'center', border: `2px solid ${s.color}18`, cursor: s.clickable ? 'pointer' : 'default', transition: 'transform 0.2s' }}>
                    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{s.icon}</div>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: '2.2rem', color: s.color, letterSpacing: '1px', lineHeight: 1 }}>{s.val}</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--gray-text)', marginTop: '0.3rem', fontFamily: 'var(--font-cond)', fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase' }}>{s.label}</div>
                  </motion.div>
                ))}
              </div>
            )}

            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', color: 'var(--blue-dark)', letterSpacing: '1px', marginBottom: '1.5rem' }}>
              DEMANDES REÇUES ({bookings.length})
            </h2>

            {bookings.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '4rem 2rem', background: 'white', borderRadius: 16 }}>
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📭</div>
                <p style={{ color: 'var(--gray-text)' }}>Aucune demande pour l'instant. Complétez votre profil pour attirer plus de clients !</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {bookings.map((b, i) => (
                  <motion.div key={b.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                    style={{ background: 'white', borderRadius: 12, padding: '1.5rem', boxShadow: 'var(--shadow-sm)', border: `2px solid ${b.status === 'PENDING' ? 'rgba(245,158,11,0.3)' : 'rgba(26,59,93,0.06)'}`, display: 'flex', gap: '1.2rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 180 }}>
                      {b.status === 'PENDING' && (
                        <div style={{ display: 'inline-block', background: 'rgba(245,158,11,0.1)', color: '#D97706', fontSize: '0.68rem', fontFamily: 'var(--font-cond)', fontWeight: 700, letterSpacing: '1px', padding: '0.2rem 0.6rem', borderRadius: '20px', marginBottom: '0.4rem' }}>
                          🔔 NOUVELLE DEMANDE
                        </div>
                      )}
                      <div style={{ fontFamily: 'var(--font-cond)', fontWeight: 700, fontSize: '1rem', color: 'var(--blue-dark)', marginBottom: '0.2rem' }}>
                        {b.client?.firstName} {b.client?.lastName} — {b.client?.city}
                      </div>
                      <div style={{ fontSize: '0.83rem', color: 'var(--gray-text)' }}>{b.service?.replace(/_/g, ' ')}</div>
                      {b.description && <div style={{ fontSize: '0.8rem', color: 'var(--gray-text)', marginTop: '0.3rem', fontStyle: 'italic' }}>"{b.description}"</div>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.75rem', color: 'var(--gray-text)' }}>{new Date(b.createdAt).toLocaleDateString('fr-FR')}</span>
                      <span style={{ padding: '0.3rem 0.8rem', borderRadius: 20, fontSize: '0.72rem', fontFamily: 'var(--font-cond)', fontWeight: 700, background: `${STATUS_LABELS[b.status]?.color}18`, color: STATUS_LABELS[b.status]?.color }}>
                        {STATUS_LABELS[b.status]?.icon} {STATUS_LABELS[b.status]?.label}
                      </span>

                      {/* Actions selon statut */}
                      {b.status === 'PENDING' && (
                        <>
                          <button onClick={() => handleUpdateStatus(b.id, 'CONFIRMED')} disabled={updatingStatus === b.id}
                            style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', color: '#10B981', padding: '0.4rem 0.9rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, fontFamily: 'var(--font-cond)' }}>
                            ✅ Confirmer
                          </button>
                          <button onClick={() => handleUpdateStatus(b.id, 'CANCELLED')} disabled={updatingStatus === b.id}
                            style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#EF4444', padding: '0.4rem 0.9rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, fontFamily: 'var(--font-cond)' }}>
                            ❌ Refuser
                          </button>
                        </>
                      )}
                      {b.status === 'CONFIRMED' && (
                        <button onClick={() => handleUpdateStatus(b.id, 'IN_PROGRESS')} disabled={updatingStatus === b.id}
                          style={{ background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.3)', color: '#8B5CF6', padding: '0.4rem 0.9rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, fontFamily: 'var(--font-cond)' }}>
                          🔧 Démarrer
                        </button>
                      )}
                      {b.status === 'IN_PROGRESS' && (
                        <button onClick={() => handleUpdateStatus(b.id, 'COMPLETED')} disabled={updatingStatus === b.id}
                          style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', color: '#10B981', padding: '0.4rem 0.9rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, fontFamily: 'var(--font-cond)' }}>
                          🎉 Terminer
                        </button>
                      )}

                      {b.client?.user && (
                        <button onClick={() => openMessages(b.client.user)}
                          style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: 'rgba(255,107,0,0.1)', border: '1px solid rgba(255,107,0,0.3)', color: 'var(--orange)', padding: '0.4rem 0.9rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>
                          <MessageCircle size={14} /> Répondre
                        </button>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'facturation' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', color: 'var(--blue-dark)', letterSpacing: '1px' }}>
                FACTURATION ({invoices.length})
              </h2>
              <button onClick={() => setShowInvoiceForm(true)}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'linear-gradient(135deg, #FF6B00, #CC4400)', border: 'none', color: 'white', padding: '0.7rem 1.4rem', borderRadius: '10px', cursor: 'pointer', fontFamily: 'var(--font-cond)', fontWeight: 700, fontSize: '0.9rem', letterSpacing: '1px', boxShadow: '0 4px 16px rgba(255,107,0,0.3)' }}>
                <Plus size={16} /> NOUVELLE FACTURE
              </button>
            </div>

            {/* FORMULAIRE NOUVELLE FACTURE */}
            <AnimatePresence>
              {showInvoiceForm && (
                <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
                  style={{ background: 'white', borderRadius: 16, padding: '2rem', boxShadow: 'var(--shadow-md)', border: '2px solid rgba(255,107,0,0.15)', marginBottom: '2rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                    <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: 'var(--blue-dark)', letterSpacing: '1px' }}>🧾 NOUVELLE FACTURE</h3>
                    <button onClick={() => setShowInvoiceForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-text)' }}><X size={20} /></button>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px,1fr))', gap:'1rem' }}>
                    <div style={{ gridColumn:'1 / -1' }}>
                      <label style={{ fontSize:'0.75rem', fontWeight:700, letterSpacing:'1px', textTransform:'uppercase', color:'var(--blue-dark)', display:'block', marginBottom:'0.4rem' }}>Lier à une commande</label>
                      <select value={invoiceForm.bookingId} onChange={e => {
                        const b = bookings.find(bk => bk.id === e.target.value);
                        if (b) setInvoiceForm(p => ({ ...p, bookingId:b.id, clientName:`${b.client?.firstName||''} ${b.client?.lastName||''}`.trim(), clientPhone:b.client?.user?.phone||'', service:b.service||p.service, clientUserId:b.client?.user?.id||'' }));
                        else setInvoiceForm(p => ({ ...p, bookingId:'' }));
                      }} className="form-select">
                        <option value="">Saisie manuelle</option>
                        {bookings.filter(b => b.status==='COMPLETED'||b.status==='IN_PROGRESS').map(b => (
                          <option key={b.id} value={b.id}>{b.client?.firstName} {b.client?.lastName} — {b.service?.replace(/_/g,' ')}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize:'0.75rem', fontWeight:700, letterSpacing:'1px', textTransform:'uppercase', color:'var(--blue-dark)', display:'block', marginBottom:'0.4rem' }}>Nom du client *</label>
                      <input value={invoiceForm.clientName} onChange={e => setInvoiceForm(p => ({ ...p, clientName: e.target.value }))}
                        placeholder="Ex: Kofi Mensah" className="form-input" />
                    </div>
                    <div>
                      <label style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--blue-dark)', display: 'block', marginBottom: '0.4rem' }}>Téléphone client</label>
                      <input value={invoiceForm.clientPhone} onChange={e => setInvoiceForm(p => ({ ...p, clientPhone: e.target.value }))}
                        placeholder="+229 XX XX XX XX" className="form-input" />
                    </div>
                    <div>
                      <label style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--blue-dark)', display: 'block', marginBottom: '0.4rem' }}>Service *</label>
                      <select value={invoiceForm.service} onChange={e => setInvoiceForm(p => ({ ...p, service: e.target.value }))} className="form-select">
                        <option value="">Choisir un service</option>
                        {SERVICES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--blue-dark)', display: 'block', marginBottom: '0.4rem' }}>Montant (FCFA) *</label>
                      <input type="number" value={invoiceForm.amount} onChange={e => setInvoiceForm(p => ({ ...p, amount: e.target.value }))}
                        placeholder="Ex: 15000" className="form-input" />
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--blue-dark)', display: 'block', marginBottom: '0.4rem' }}>Description des travaux</label>
                      <textarea value={invoiceForm.description} onChange={e => setInvoiceForm(p => ({ ...p, description: e.target.value }))}
                        placeholder="Ex: Vidange complète + remplacement filtre à huile" rows={3} className="form-textarea" />
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.8rem', marginTop: '1.5rem' }}>
                    <button onClick={() => setShowInvoiceForm(false)}
                      style={{ background: 'none', border: '2px solid var(--gray-mid)', color: 'var(--gray-text)', padding: '0.7rem 1.4rem', borderRadius: '10px', cursor: 'pointer', fontFamily: 'var(--font-cond)', fontWeight: 700 }}>
                      Annuler
                    </button>
                    <button onClick={handleCreateInvoice} disabled={savingInvoice}
                      style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'linear-gradient(135deg, #FF6B00, #CC4400)', border: 'none', color: 'white', padding: '0.7rem 1.4rem', borderRadius: '10px', cursor: 'pointer', fontFamily: 'var(--font-cond)', fontWeight: 700, opacity: savingInvoice ? 0.7 : 1 }}>
                      <FileText size={15} /> {savingInvoice ? 'Création...' : 'CRÉER LA FACTURE'}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* LISTE DES FACTURES */}
            {invoices.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '4rem 2rem', background: 'white', borderRadius: 16 }}>
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🧾</div>
                <p style={{ color: 'var(--gray-text)' }}>Aucune facture créée pour l'instant.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {invoices.map((inv, i) => (
                  <motion.div key={inv.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                    style={{ background: 'white', borderRadius: 12, padding: '1.5rem', boxShadow: 'var(--shadow-sm)', border: `2px solid ${inv.status === 'PAID' ? 'rgba(16,185,129,0.2)' : 'rgba(245,158,11,0.2)'}`, display: 'flex', gap: '1.2rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ width: 48, height: 48, borderRadius: '50%', background: inv.status === 'PAID' ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <FileText size={22} color={inv.status === 'PAID' ? '#10B981' : '#F59E0B'} />
                    </div>
                    <div style={{ flex: 1, minWidth: 180 }}>
                      <div style={{ fontFamily: 'var(--font-cond)', fontWeight: 700, fontSize: '1rem', color: 'var(--blue-dark)', marginBottom: '0.2rem' }}>
                        {inv.clientName} {inv.clientPhone && <span style={{ color: 'var(--gray-text)', fontSize: '0.85rem' }}>· {inv.clientPhone}</span>}
                      </div>
                      <div style={{ fontSize: '0.83rem', color: 'var(--gray-text)' }}>{inv.service?.replace(/_/g, ' ')}</div>
                      {inv.description && <div style={{ fontSize: '0.78rem', color: 'var(--gray-text)', marginTop: '0.2rem', fontStyle: 'italic' }}>{inv.description}</div>}
                      <div style={{ fontSize: '0.72rem', color: 'rgba(0,0,0,0.3)', marginTop: '0.3rem' }}>{new Date(inv.createdAt).toLocaleDateString('fr-FR')}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', color: 'var(--blue-dark)', letterSpacing: '1px', lineHeight: 1 }}>
                          {inv.amount?.toLocaleString('fr-FR')} FCFA
                        </div>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.25rem 0.7rem', borderRadius: 20, fontSize: '0.7rem', fontFamily: 'var(--font-cond)', fontWeight: 700, background: inv.status === 'PAID' ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)', color: inv.status === 'PAID' ? '#10B981' : '#F59E0B' }}>
                          {inv.status === 'PAID' ? <><CheckCircle size={11} /> PAYÉE</> : <><Clock size={11} /> EN ATTENTE</>}
                        </span>
                      </div>
                      <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
                        {/* Envoyer sur le site (messagerie interne) */}
                        {(inv.clientUserId || inv.bookingId) && (
                          <button onClick={async () => {
                            try {
                              await api.post(`/invoices/${inv.id}/send`);
                              toast.success('Facture envoyée au client dans la messagerie !');
                            } catch { toast.error('Impossible d\'envoyer : client introuvable.'); }
                          }}
                            style={{ display:'flex', alignItems:'center', gap:'0.4rem', background:'rgba(59,130,246,0.1)', border:'1px solid rgba(59,130,246,0.3)', color:'#3B82F6', padding:'0.5rem 1rem', borderRadius:'8px', cursor:'pointer', fontSize:'0.78rem', fontWeight:700, fontFamily:'var(--font-cond)', whiteSpace:'nowrap' }}>
                            ✉️ Envoyer sur le site
                          </button>
                        )}
                        {/* WhatsApp */}
                        {inv.clientPhone && (
                          <a href={`https://wa.me/${inv.clientPhone.replace(/\D/g,'')}?text=${encodeURIComponent('🧾 Facture ELIM DÉPANNAGE\nService: '+inv.service?.replace(/_/g,' ')+(inv.description?'\n'+inv.description:'')+'\n💰 Montant: '+inv.amount?.toLocaleString('fr-FR')+' FCFA\nMerci !')}`}
                            target="_blank" rel="noopener noreferrer"
                            style={{ display:'flex', alignItems:'center', gap:'0.4rem', background:'rgba(37,211,102,0.1)', border:'1px solid rgba(37,211,102,0.3)', color:'#25D366', padding:'0.5rem 1rem', borderRadius:'8px', fontSize:'0.78rem', fontWeight:700, fontFamily:'var(--font-cond)', whiteSpace:'nowrap', textDecoration:'none' }}>
                            💬 WhatsApp
                          </a>
                        )}
                        {/* Marquer payée */}
                        {inv.status !== 'PAID' && (
                          <button onClick={() => handleMarkPaid(inv.id)}
                            style={{ display:'flex', alignItems:'center', gap:'0.4rem', background:'rgba(16,185,129,0.1)', border:'1px solid rgba(16,185,129,0.3)', color:'#10B981', padding:'0.5rem 1rem', borderRadius:'8px', cursor:'pointer', fontSize:'0.78rem', fontWeight:700, fontFamily:'var(--font-cond)', whiteSpace:'nowrap' }}>
                            ✅ Marquer payée
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'messages' && <MessagingPanel initialContact={messageContact} />}
      </div>
    </div>
  );
}

export default DashboardClient;
